import * as domUtils from '../util/dom';
import extend from '../util/extend';
import debugUtil from '../util/debug';
import statsUtil from '../util/stats';
import Config from '../config';
import Sizzle from 'sizzle';
import { readCookie } from '../util/cookie';

const Debug = debugUtil('chromecast-video');

const getVideoElement = (api) => Sizzle('video', api.element())[0];

const ADOBE_VISITOR_ID_COOKIE_NAME = 's_vi';

const isVideoIdLoadedOnChromecast = (videoId, player) => (
    player.isMediaLoaded &&
    player.mediaInfo &&
    player.mediaInfo.contentId === videoId
);

const getPosterUrlFromAPI = (api) => {
    let posterUrl = getVideoElement(api).getAttribute('poster') || Config.defaultThumbnail;
    if (posterUrl.indexOf('http') === -1) {
        posterUrl = 'http:' + posterUrl;
    }
};

const fireAPIEventByPlayerState = (api, player) => {
    Debug.log('fireAPIEventByPlayerState', player.playerState, api.data().videoId);
    if (isVideoIdLoadedOnChromecast(api.data().videoId, player)) {
        switch (player.playerState) {
            case chrome.cast.media.PlayerState.BUFFERING:
                api.fire('buffering');
                break;
            case chrome.cast.media.PlayerState.PAUSED:
                api.fire('pause');
                break;
            case chrome.cast.media.PlayerState.PLAYING:
                api.fire('playing');
                break;
        }
    }
};

const loadMediaOnChromecast = (api, currentTime) => {
    const videoId = api.data().videoId;
    const mediaInfo = new chrome.cast.media.MediaInfo(videoId, 'application/vnd.apple.mpegurl');

    const metadata = new chrome.cast.media.GenericMediaMetadata();
    const posterUrl = getPosterUrlFromAPI(api);
    metadata.images = [
        new chrome.cast.Image(posterUrl),
        new chrome.cast.Image(posterUrl)
    ];
    mediaInfo.metadata = metadata;

    const loadRequest = new chrome.cast.media.LoadRequest(mediaInfo);

    loadRequest.customData = {
        subsite: 'svtplay',
        launchAppName: 'webb',
        launchDevice: 'webb',
        client: statsUtil.getClient(),
        adobeVisitorId: readCookie(ADOBE_VISITOR_ID_COOKIE_NAME)
    };

    if (window.SVT_STATISTICS && window.SVT_STATISTICS.contextData) {
        const { contextData } = window.SVT_STATISTICS;

        loadRequest.customData.svt_domain = contextData.svt_domain;
        loadRequest.customData.svt_level1 = contextData.svt_level1;
        loadRequest.customData.launchAppName = contextData.svt_appname;
    }


    if (currentTime) {
        loadRequest.currentTime = currentTime;
    }

    const castSession = cast.framework.CastContext.getInstance().getCurrentSession();

    Debug.log(`loadMedia videoId=${videoId} currentTime=${currentTime}`);
    castSession.loadMedia(loadRequest).then(
        () => Debug.log('Load media success.'),
        (errorCode) => Debug.error(`Load media failed: ${errorCode}.`)
    );
};

const createVideoExtension = (api, player, controller) => ({
    play: () => {
        if (isVideoIdLoadedOnChromecast(api.data().videoId, player)) {
            Debug.log('Play with playOrPause because video is already loaded.');
            controller.playOrPause();
        } else {
            Debug.log('Play with loadMediaOnChromecast because video is not loaded.');
            loadMediaOnChromecast(api, 0);
        }

        return true;
    },
    pause: () => {
        controller.playOrPause();

        return true;
    },
    seek: (position) => {
        Debug.log(`Seek to ${position}.`);
        player.currentTime = position;
        controller.seek();

        return true;
    },
    getSrc: () => {
        return (player.mediaInfo && player.mediaInfo.contentUrl) || '';
    },
    getDuration: () => {
        if (player.mediaInfo &&
            player.mediaInfo.customData &&
            player.mediaInfo.customData.response &&
            player.mediaInfo.customData.response.contentDuration) {
            return player.mediaInfo.customData.response.contentDuration;
        }

        return player.duration;
    },
    getCurrentTime: () => {
        return player.currentTime;
    },
    getVolume: () => {
        return typeof player.volumeLevel === 'number' ? player.volumeLevel : 0;
    },
    getMaxSeekable: () => {
        return 10;
    },
    getMetrics: () => {
        return {
            flash: false,
            dash: false,
            droppedFrames: -1,
            dimensions: -1,
            resolution: -1,
            bufferLevel: -1
        };
    },
    getAudioTracks: () => {
        // Get currently active track ids.
        const castSession = cast.framework.CastContext.getInstance().getCurrentSession();
        const mediaSession = castSession.getMediaSession();
        if (!mediaSession) {
            return [];
        }

        const { activeTrackIds } = mediaSession;

        const audioTracks = player.mediaInfo.tracks
            .filter((track) => track.type === chrome.cast.media.TrackType.AUDIO)
            .map((track) => ({
                id: track.language,
                active: activeTrackIds.includes(track.trackId)
            }));

        return audioTracks;
    },
    getTextTracks: () => {
        if (!player.mediaInfo) {
            return [];
        }

        return player.mediaInfo.tracks
            .filter((track) => track.type === chrome.cast.media.TrackType.TEXT)
            .map((track) => ({ id: track.language }));
    },
    isMuted: () => {
        return player.isMuted;
    },
    isPaused: () => {
        return player.isPaused;
    },
    mute: (shouldMute) => {
        controller.muteOrUnmute();

        return true;
    },
    showCaptions: (shouldShow) => {
        if (!player.mediaInfo) {
            return true;
        }

        // Get available text track ids.
        const textTrackIds = player.mediaInfo.tracks.filter((track) => (
            track.type === chrome.cast.media.TrackType.TEXT
        )).map((track) => track.trackId);

        // Get currently active track ids.
        const castSession = cast.framework.CastContext.getInstance().getCurrentSession();
        const mediaSession = castSession.getMediaSession();
        const { activeTrackIds } = mediaSession;

        // Check if the current state differ from wanted state.
        const isShowing = textTrackIds.length > 0 && activeTrackIds.includes(textTrackIds[0]);
        if (Boolean(shouldShow) === isShowing) {
            Debug.log('Change active tracks not required.');

            return;
        }

        // Get new array of active track ids by either adding a text track id or
        // removing all text tracks.
        const newActiveTrackIds = shouldShow ?
            [...activeTrackIds, textTrackIds[0]] :
            activeTrackIds.filter((trackId) => (
                !textTrackIds.includes(trackId)
            ));

        Debug.log('Change active tracks from', activeTrackIds, 'to', newActiveTrackIds);

        // Send request to change tracks.
        const tracksInfoRequest = new chrome.cast.media.EditTracksInfoRequest(
            newActiveTrackIds
        );
        mediaSession.editTracksInfo(tracksInfoRequest,
            () => Debug.log('editTracksInfo success'),
            () => Debug.error('editTracksInfo failed')
        );

        return true;
    },
    setAudioTrack: (language) => {
        // Get track with the specified language.
        const track = player.mediaInfo.tracks.find((track) => (
            track.type === chrome.cast.media.TrackType.AUDIO &&
            track.language === language
        ));

        if (track === undefined) {
            Debug.warn(`Failed to set audio track to ${language}.`);

            return true;
        }

        // Get audio track ids.
        const audioTrackIds = player.mediaInfo.tracks.filter((track) => (
            track.type === chrome.cast.media.TrackType.AUDIO
        )).map((track) => track.trackId);

        // Get currently active track ids.
        const castSession = cast.framework.CastContext.getInstance().getCurrentSession();
        const mediaSession = castSession.getMediaSession();
        const { activeTrackIds } = mediaSession;

        // Get new activeTrackIds by removing all audio tracks, and then adding
        // the new one.
        const newActiveTrackIds = activeTrackIds
            .filter((trackId) => !audioTrackIds.includes(trackId));
        newActiveTrackIds.push(track.trackId);

        Debug.log('Change active tracks from', activeTrackIds, 'to', newActiveTrackIds);

        // Send request to change tracks.
        const tracksInfoRequest = new chrome.cast.media.EditTracksInfoRequest(
            newActiveTrackIds
        );
        mediaSession.editTracksInfo(tracksInfoRequest,
            () => Debug.log('editTracksInfo success'),
            () => Debug.error('editTracksInfo failed')
        );

        return true;
    },
    setVolume: (volume) => {
        player.volumeLevel = volume;
        controller.setVolumeLevel();

        return true;
    },
    reset: () => {
        // const { videoId } = api.data();
        // if (videoId) {
        //     api.load(videoId, false);
        // }

        return true;
    },
    unload: () => {
        // See implementation in load() below.
        return true;
    }
});

const shouldLoadVideo = (videoId, api, player) => {
    /*
     * Sometimes player.isConnected can be true, but getCurrentSession()
     * might still return null or undefined (for unknown reasons).
     */
    const castSession = cast.framework.CastContext.getInstance().getCurrentSession();
    if (!castSession) {
        Debug.warn('Failed to load video because the current session is unavailable.');

        return true;
    }
    const mediaSession = castSession.getMediaSession();

    const isAlreadyLoaded = isVideoIdLoadedOnChromecast(videoId, player);
    const isAlreadyLoading = (mediaSession && mediaSession.loadingItemId === videoId);

    Debug.log(`Load: videoId=${videoId} autoplay=${api.isAutoplay()} loaded=${isAlreadyLoaded} loading=${isAlreadyLoading}`);

    return Boolean(api.isAutoplay() && !isAlreadyLoaded && !isAlreadyLoading);
};

const attachExtension = (target, extension) => {
    const originalValues = Object.keys(extension).map((key) => ({ key, value: target[key] }));
    extend(target, extension);

    const detachExtension = () => {
        originalValues.forEach(({key, value}) => {
            target[key] = value;
        });
    };

    return detachExtension;
};

const listenToController = (videoId, videoElement, api, player, controller) => {
    const handleMediaInfoChanged = (e) => {
        if (!e.value) {
            Debug.log('handleMediaInfoChanged with falsy value.');

            return;
        }

        Debug.log(`handleMediaInfoChanged value=${e.value}`);

        if (!player.mediaInfo || player.mediaInfo.contentId !== videoId) {
            Debug.log(
                'mediaInfo contentId does not match: ' +
                `mediaInfo.contentId=${player.mediaInfo && player.mediaInfo.contentId} videoId=${videoId}`
            );

            return;
        }

        if (!api.state().ready) {
            Debug.log('Fire loadstart');
            videoElement.dispatchEvent(
                new Event('loadstart')
            );
        }

        fireAPIEventByPlayerState(api, player);

        // Check if text tracks are available.
        if(player.mediaInfo.tracks.some((track) => track.type === chrome.cast.media.TrackType.TEXT)) {
            api.fire('captionsloaded', { fromManifest: false });
        }

        // Check if audio tracks are availabke.
        if (player.mediaInfo.tracks.some((track) => track.type === chrome.cast.media.TrackType.AUDIO)) {
            api.fire('audiotracksloaded');
        }
    };

    const handlePlayerStateChanged  = (e) => {
        fireAPIEventByPlayerState(api, player);
    };

    const handleCurrentTimeChanged = (e) => {
        api.fire('timeupdate', e.value);

        if (e.value >= player.duration - 1) {
            videoElement.dispatchEvent(
                new Event('ended')
            );
        }
    };

    controller.addEventListener(
        cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED,
        handlePlayerStateChanged
    );

    controller.addEventListener(
        cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED,
        handleCurrentTimeChanged
    );

    controller.addEventListener(
        cast.framework.RemotePlayerEventType.MEDIA_INFO_CHANGED,
        handleMediaInfoChanged
    );

    const unlisten = () => {
        controller.removeEventListener(
            cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED,
            handlePlayerStateChanged
        );

        controller.removeEventListener(
            cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED,
            handleCurrentTimeChanged
        );

        controller.removeEventListener(
            cast.framework.RemotePlayerEventType.MEDIA_INFO_CHANGED,
            handleMediaInfoChanged
        );
    };

    return unlisten;
};

export const load = (videoId, videoElement, api, options) => {
    const { player, controller } = options.chromecast;
    window.player = player;
    window.controller = controller;


    if (videoElement.unload) {
        videoElement.unload();
    }

    Debug.log('Attaching listeners to controller.');

    const unlisten = listenToController(videoId, videoElement, api, player, controller);

    // TODO: Only extend if not yet extended.
    let detachExtension = null;
    const extension = createVideoExtension(api, player, controller);
    extension.unload = () => {
        Debug.log('Unload');
        unlisten();

        if (detachExtension) {
            detachExtension();
        }

        return true;
    };

    detachExtension = attachExtension(videoElement, extension);

    if (shouldLoadVideo(videoId, api, player)) {
        loadMediaOnChromecast(api, options && options.startPosition);
    } else {
        // Fake loadstart
        window.setTimeout(() => {
            /*
             * By dispatching a loadstart event we will trigger the ready
             * handler in the wrapper api.
             */
            videoElement.dispatchEvent(
                new Event('loadstart')
            );

            if (isVideoIdLoadedOnChromecast(videoId, player)) {
                /*
                 * The video may be paused, and some internal code require a playing
                 * and timeupdate event to fully initialize (e.g. UI timeline).
                 *
                 * The following will fake those events, with the intention of
                 * faking that the video has been played before. Note that this
                 * should only happend if this is the currently loaded video on the
                 * chromecast.
                 */
                api.fire('playing');
                api.fire('timeupdate', player.currentTime);

                // Trigger event for actual state: buffering, paused or playing.
                fireAPIEventByPlayerState(api, player);
            }

        }, 0);
    }

    return videoElement;
};




// WEBPACK FOOTER //
// ./src/js/modules/chromecast-video.js